import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import de from '../locales/de';
import en from '../locales/en';

const translations = { de, en };
const supportedLanguages = ['de', 'en'];

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    if (saved && supportedLanguages.includes(saved)) {
      return saved;
    }
    const browserLang = navigator.language.split('-')[0];
    if (supportedLanguages.includes(browserLang)) {
      return browserLang;
    }
    return 'de';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = keys.reduce((obj, k) => obj?.[k], translations['de']);
        break;
      }
    }

    if (typeof value !== 'string') {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    return value.replace(/\{(\w+)}/g, (match, param) => {
      return params[param] !== undefined ? params[param] : match;
    });
  }, [language]);

  const changeLanguage = (lang) => {
    if (supportedLanguages.includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
        t,
        supportedLanguages
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
